/**
 * Offline Manager
 * Handles offline detection and cached data fallbacks
 */

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
  version: string;
}

export interface OfflineConfig {
  enableOfflineMode: boolean;
  cacheExpiration: {
    exchangeRates: number;
    providerStatus: number;
    dashboardStats: number;
    paymentHistory: number;
    default: number;
  };
  maxCacheSize: number;
  syncOnReconnect: boolean;
}

export const defaultOfflineConfig: OfflineConfig = {
  enableOfflineMode: true,
  cacheExpiration: {
    exchangeRates: 5 * 60 * 1000, // 5 minutes
    providerStatus: 30 * 1000, // 30 seconds
    dashboardStats: 10 * 60 * 1000, // 10 minutes
    paymentHistory: 60 * 60 * 1000, // 1 hour
    default: 5 * 60 * 1000 // 5 minutes
  },
  maxCacheSize: 50, // Maximum number of cache entries
  syncOnReconnect: true
};

/**
 * Offline Manager Class
 */
export class OfflineManager {
  private static instance: OfflineManager;
  private cache = new Map<string, CacheEntry<any>>();
  private config: OfflineConfig;
  private isOnline = navigator.onLine;
  private listeners: Array<(isOnline: boolean) => void> = [];
  private pendingRequests: Array<() => Promise<void>> = [];

  constructor(config: OfflineConfig = defaultOfflineConfig) {
    this.config = config;
    this.setupEventListeners();
    this.startCacheCleanup();
  }

  static getInstance(config?: OfflineConfig): OfflineManager {
    if (!OfflineManager.instance) {
      OfflineManager.instance = new OfflineManager(config);
    }
    return OfflineManager.instance;
  }

  /**
   * Setup online/offline event listeners
   */
  private setupEventListeners(): void {
    window.addEventListener('online', () => {
      console.log('🌐 Connection restored');
      this.isOnline = true;
      this.notifyListeners(true);
      
      if (this.config.syncOnReconnect) {
        this.syncPendingRequests();
      }
    });

    window.addEventListener('offline', () => {
      console.log('📴 Connection lost - switching to offline mode');
      this.isOnline = false;
      this.notifyListeners(false);
    });

    // Additional connectivity checks
    this.startConnectivityMonitoring();
  }

  /**
   * Start periodic connectivity monitoring
   */
  private startConnectivityMonitoring(): void {
    setInterval(async () => {
      const wasOnline = this.isOnline;
      this.isOnline = await this.checkConnectivity();
      
      if (wasOnline !== this.isOnline) {
        this.notifyListeners(this.isOnline);
      }
    }, 10000); // Check every 10 seconds
  }

  /**
   * Check actual connectivity (not just navigator.onLine)
   */
  private async checkConnectivity(): Promise<boolean> {
    if (!navigator.onLine) {
      return false;
    }

    try {
      // Try to fetch a small resource to verify connectivity
      const response = await fetch('/favicon.ico', {
        method: 'HEAD',
        cache: 'no-cache',
        signal: AbortSignal.timeout(5000)
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Add online/offline status listener
   */
  addStatusListener(listener: (isOnline: boolean) => void): () => void {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of status change
   */
  private notifyListeners(isOnline: boolean): void {
    this.listeners.forEach(listener => {
      try {
        listener(isOnline);
      } catch (error) {
        console.error('Error in offline status listener:', error);
      }
    });
  }

  /**
   * Check if currently online
   */
  isConnected(): boolean {
    return this.isOnline;
  }

  /**
   * Cache data with expiration
   */
  cacheData<T>(key: string, data: T, customExpiration?: number): void {
    if (!this.config.enableOfflineMode) {
      return;
    }

    const now = Date.now();
    const expiration = customExpiration || this.getExpirationForKey(key);
    
    const entry: CacheEntry<T> = {
      data,
      timestamp: now,
      expiresAt: now + expiration,
      version: '1.0.0' // Could be used for cache versioning
    };

    this.cache.set(key, entry);
    this.enforceMaxCacheSize();

    console.log(`📦 Cached data for key: ${key} (expires in ${expiration}ms)`);
  }

  /**
   * Get cached data if available and not expired
   */
  getCachedData<T>(key: string): T | null {
    if (!this.config.enableOfflineMode) {
      return null;
    }

    const entry = this.cache.get(key) as CacheEntry<T> | undefined;
    
    if (!entry) {
      return null;
    }

    const now = Date.now();
    if (now > entry.expiresAt) {
      this.cache.delete(key);
      console.log(`🗑️ Expired cache entry removed: ${key}`);
      return null;
    }

    console.log(`📦 Retrieved cached data for key: ${key}`);
    return entry.data;
  }

  /**
   * Get cached data with metadata
   */
  getCachedEntry<T>(key: string): CacheEntry<T> | null {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;
    
    if (!entry) {
      return null;
    }

    const now = Date.now();
    if (now > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return entry;
  }

  /**
   * Check if data is cached and valid
   */
  hasCachedData(key: string): boolean {
    return this.getCachedData(key) !== null;
  }

  /**
   * Get expiration time for specific key types
   */
  private getExpirationForKey(key: string): number {
    if (key.includes('exchangeRates')) {
      return this.config.cacheExpiration.exchangeRates;
    }
    if (key.includes('providerStatus')) {
      return this.config.cacheExpiration.providerStatus;
    }
    if (key.includes('dashboardStats')) {
      return this.config.cacheExpiration.dashboardStats;
    }
    if (key.includes('paymentHistory')) {
      return this.config.cacheExpiration.paymentHistory;
    }
    return this.config.cacheExpiration.default;
  }

  /**
   * Enforce maximum cache size
   */
  private enforceMaxCacheSize(): void {
    if (this.cache.size <= this.config.maxCacheSize) {
      return;
    }

    // Remove oldest entries
    const entries = Array.from(this.cache.entries())
      .sort(([, a], [, b]) => a.timestamp - b.timestamp);

    const toRemove = entries.slice(0, this.cache.size - this.config.maxCacheSize);
    toRemove.forEach(([key]) => {
      this.cache.delete(key);
      console.log(`🗑️ Removed old cache entry: ${key}`);
    });
  }

  /**
   * Start periodic cache cleanup
   */
  private startCacheCleanup(): void {
    setInterval(() => {
      this.cleanupExpiredEntries();
    }, 60000); // Cleanup every minute
  }

  /**
   * Clean up expired cache entries
   */
  private cleanupExpiredEntries(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    this.cache.forEach((entry, key) => {
      if (now > entry.expiresAt) {
        expiredKeys.push(key);
      }
    });

    expiredKeys.forEach(key => {
      this.cache.delete(key);
    });

    if (expiredKeys.length > 0) {
      console.log(`🗑️ Cleaned up ${expiredKeys.length} expired cache entries`);
    }
  }

  /**
   * Add request to pending queue for when connection is restored
   */
  addPendingRequest(request: () => Promise<void>): void {
    this.pendingRequests.push(request);
  }

  /**
   * Sync pending requests when connection is restored
   */
  private async syncPendingRequests(): void {
    if (this.pendingRequests.length === 0) {
      return;
    }

    console.log(`🔄 Syncing ${this.pendingRequests.length} pending requests`);

    const requests = [...this.pendingRequests];
    this.pendingRequests = [];

    for (const request of requests) {
      try {
        await request();
      } catch (error) {
        console.error('Error syncing pending request:', error);
        // Re-add failed requests to queue
        this.pendingRequests.push(request);
      }
    }

    if (this.pendingRequests.length > 0) {
      console.log(`⚠️ ${this.pendingRequests.length} requests failed to sync and will be retried`);
    }
  }

  /**
   * Clear all cached data
   */
  clearCache(): void {
    this.cache.clear();
    console.log('🗑️ All cached data cleared');
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): {
    size: number;
    entries: Array<{
      key: string;
      size: number;
      age: number;
      expiresIn: number;
    }>;
    totalSize: number;
  } {
    const now = Date.now();
    const entries = Array.from(this.cache.entries()).map(([key, entry]) => ({
      key,
      size: JSON.stringify(entry.data).length,
      age: now - entry.timestamp,
      expiresIn: Math.max(0, entry.expiresAt - now)
    }));

    return {
      size: this.cache.size,
      entries,
      totalSize: entries.reduce((sum, entry) => sum + entry.size, 0)
    };
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<OfflineConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get current configuration
   */
  getConfig(): OfflineConfig {
    return { ...this.config };
  }
}

// Export singleton instance
export const offlineManager = OfflineManager.getInstance();